// src/pages/Home.jsx
import React, { useEffect, useState, useRef } from "react";
import { signIn } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from "lucide-react";
import "../styles/Home.css";
import { storeAccessToken } from '../utils/authUtils';
import Footer from '../components/Footer';

function Home() {
  const DISABLE_AUTH_ACTIONS = true;
  const HIDE_AUTH_ACTIONS = true;
  const APP_STORE_URL = "https://apps.apple.com/us/app/skroop/id6754806438";

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();
  const rotatingContainerRef = useRef(null);
  const animationIntervalRef = useRef(null);
  const timeoutRefs = useRef([]);

  const handleKeyDown = (e) => {
    if (process.env.NODE_ENV === 'development') {
      console.log("Enter key pressed – trigger login");
    }

    if (e.key === "Enter" && email && password) {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    setLoginError('');
    try {
      const user = await signIn({ username: email, password });
      if (process.env.NODE_ENV === 'development') {
        console.log("Signed in:", user);
      }
      
      await storeAccessToken();

      setShowLoginModal(false);
      navigate('/dashboard/profile');
    } catch (err) {
      console.error("Cognito login failed:", err);
      setLoginError("Login failed. Please check your email and password.");
    }
  };

  const handleAppleLogin = () => {
    // TODO: Implement once Apple Developer account is approved
    alert('Apple Sign-In coming soon!');
  };

  useEffect(() => {
    const words = ["God.", "Jesus.", "morality."];
    let index = 0;
    const container = rotatingContainerRef.current;

    function animateWordOut(callback) {
      const letters = container?.querySelectorAll("span") || [];
      let i = letters.length - 1;
      const interval = setInterval(() => {
        if (i < 0) {
          clearInterval(interval);
          callback();
          return;
        }
        letters[i].classList.add("fade-out");
        i--;
      }, 50);
    }

    function animateWordIn(word) {
      if (!container) return;
      // Clear existing content safely
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      // Clear any existing timeouts
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current = [];
      
      [...word].forEach((char, i) => {
        const span = document.createElement("span");
        span.textContent = char; // Safe text content assignment
        span.className = "hidden-char fade-in";
        container.appendChild(span);
        const timeout = setTimeout(() => {
          span.classList.remove("hidden-char");
        }, i * 50);
        timeoutRefs.current.push(timeout);
      });
    }

    function rotateWords() {
      animateWordOut(() => {
        index = (index + 1) % words.length;
        animateWordIn(words[index]);
      });
    }

    if (container) {
      animateWordIn(words[index]);
      animationIntervalRef.current = setInterval(rotateWords, 2000);
    }

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Separate useEffect for scroll handling
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector(".header");
      const currentY = window.scrollY;

      if (header) {
        if (currentY > 50) {
          header.classList.add("header--scrolled");
          header.classList.remove("header--transparent");
        } else {
          header.classList.remove("header--scrolled");
          header.classList.add("header--transparent");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initialize on mount

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);



  return (
    <div className="home-container">
      {/* Header */}
      <header className="header">
        <div className="header__background" />
        <div className="header__content">
          <div className="header__branding">
            <a href="/">
              <img src="/images/skroop_white_transparent_thick.png" alt="Skroop Logo" className="header__logo" />
            </a>
            <a href="/"><span className="header__title">Skroop</span></a>
          </div>
          <div className="header__actions">
            <a
              href={APP_STORE_URL}
              className="header__appstore"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (APP_STORE_URL === "#") {
                  e.preventDefault();
                }
              }}
            >
              <img
                src="/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                alt="Download on the App Store"
              />
            </a>
            {!HIDE_AUTH_ACTIONS && (
              <>
                <button
                  className="header__login"
                  onClick={() => setShowLoginModal(true)}
                  disabled={DISABLE_AUTH_ACTIONS}
                  style={DISABLE_AUTH_ACTIONS ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                >
                  Login
                </button>
                <a
                  href="/signup"
                  className="header__cta"
                  aria-disabled={DISABLE_AUTH_ACTIONS}
                  onClick={(e) => {
                    if (DISABLE_AUTH_ACTIONS) {
                      e.preventDefault();
                    }
                  }}
                  style={DISABLE_AUTH_ACTIONS ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                >
                  Try Skroop Free
                </a>
              </>
            )}
          </div>
        </div>
      </header>


      {/* Hero Section */}
      <section className="hero"
      style={{
        //backgroundImage: 'url("/images/hero01.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'}}
      >
        <div className="hero__content">
          <h1 className="hero__heading">
            The modern philosophy of <span ref={rotatingContainerRef} className="hero__rotating-text"></span>
          </h1>
          <p className="hero__subheading">
            A renaissance of existence and virtue — precise and principled.
          </p>
          <div className="hero__buttons">
            {/*<a href="/signup" className="button button--primary">Get Started</a>*/}
            <a href="#faq" className="button button--secondary">Learn More</a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta__content">
          <h2 className="cta__heading">
            The daily homily, grounded in clarity and virtue.
          </h2>
          <p className="cta__subheading">
            Receive reflections on the Gospel, stimulated by reimagined divinity and recasted morality.
          </p>
          {/*<a href="/signup" className="cta__button">Get Started Today</a>*/}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing">
            <a
              href={APP_STORE_URL}
              className="header__appstore"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (APP_STORE_URL === "#") {
                  e.preventDefault();
                }
              }}
              >
              <img
                src="/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                alt="Download on the App Store"
              />
            </a>
        </section>
          {/*<div className="pricing__content">
            <h2 className="pricing__heading">Start your free trial of Skroop Premium.</h2>

            <div className="pricing__plans">
              <div className="pricing__plan pricing__plan--highlighted">
                <span className="pricing__tag">7-Day Free Trial</span>
                <h3 className="pricing__plan-title">Yearly</h3>
                <p className="pricing__plan-now">$50/yr</p>
                <p className="pricing__plan-monthly">$4.17/mo</p>
              </div>
              <div className="pricing__plan">
                <h3 className="pricing__plan-title">Monthly</h3>
                <p className="pricing__plan-now">$5/mo</p>
              </div>
            </div>

            <p className="pricing__disclaimer">
              After your free trial, the yearly subscription is $50 and renews each year unless canceled.
              <a href="/terms">Terms</a>
            </p>

            <a href="/subscribe" className="pricing__cta">Continue</a>
          </div>
      

      {/* FAQ Section */}
      <section className="faq" id="faq">
        <div className="faq__content">
          <h2 className="faq__heading">FAQs</h2>

          <div className="faq__group">
            <h3 className="faq__group-title">Tenets</h3>
            {[
              {
                question: "What is God?",
                answer: (
                          <>
                            <p>
                              God is not a myth or religious figure. God is the total set of fundamental principles that govern the universe. Gravity, electromagnetism, entropy, evolution...forces and principles that beget existence.
                            </p>
                            <p>
                              We do not contradict God via science, we reveal it.
                            </p>
                            <p>
                              God is not supernatural. God is the interaction of all natural laws; some we can explain, some we cannot, and some we may never.
                            </p>
                          </>
                        ),
              },
              {
                question: "Who is Jesus?",
                answer: "Virtue. Jesus is the personification of virtue through story.",
              },
              {
                question: "Why are you interpreting the only Gospel right now?",
                answer: (
                        <>
                          <p>
                            The Gospel is a portmanteau of <em>‘good + news’</em>, or stories of virtue. Although stories of virtue are omnipresent in cultures and civilizations, we are focusing on the Gospel stories for now.
                          </p>
                          <p>
                            Historically, the life, death and resurrection of Jesus. In reality, the Gospel are stories intended to teach us about the implementation, disregard, and restoration of morality. 
                          </p>
                        </>
                        ),  
              },
              {
                question: "What is virtue?",
                answer: (
                        <>
                          <p>
                            Skroop's foundation of virtue is built on the four cardinal virtues of Stoicism. Prudence (wisdom), Justice (fairness), Temperance (restraint) and Fortitude (courage). 
                          </p>
                          <p>
                            Our belief is that virtue is constrained to a dyad: Wisdom and Temperance. Wisdom is the ability to guide or instruct your emotions. Wisdom applied upon temperance becomes virtue. All specific virtues (e.g. courage, justics, humility, honesty, diligence...) are applied wisdom upon your environment toward rectitude. 
                          </p>         
                          <p>The most valuable aspect of virtue is to understand that it is unnatural; it takes commitment, consistency and diligence.</p>
                      </>
                      )
              },
              {
                question: "What is a sin?",
                answer: <>A sin is the expression of our natural tribal instinct. It's part of us through evolution. It is usually so natural that it's easy. A sin is not a transgression against divine law.</>
              },
              {
                question: "Heaven and Hell?",
                answer: "Heaven and hell are emotional states that we experience through life through our decisions, the practice of virtue or sin.",
              }              
            ].map((item, index) => (
              <details key={`about-${index}`} className="faq__item">
                <summary>
                  {item.question}
                  <span className="faq__icon" />
                </summary>
                <div className="faq__answer">{item.answer}</div>
              </details>
            ))}
          </div>

          <div className="faq__group">
            <h3 className="faq__group-title">General</h3>
            {[
              {
                question: "What is Skroop?",
                answer: "Skroop delivers philosophical and moral reflections based on timeless principles with modern clarity.",
              },
              {
                question: "Why now?",
                answer: "Virtue is a technology. Thousands of years ago, humans discovered that they can cooperate in large numbers, only by way of common stories. The adaptation of virtue is long overdue.",
              },
              {
                question: "What else should I know?",
                answer: "We currently are focusing on promotion of virtue of the mind. Skroop intends to be more holitic, including physical and spiritual well-being in the future.",
              },
              //{
              //  question: "Is Skroop a religion?",
              //  answer: "Based on traditional definition, no, because we do not worship.",
              //},
              //{
              //  question: "Is Skroop a cult?",
              //  answer: "In some regards, maybe. Although we have no religious veneration and devotion, one could argue we have excessive admiration for certain things.",
              //},
              //{
              //  question: "What does a Skroopist admire excessively?",
              //  answer: "The fitness of our minds and soul.",
              //},
              {
                question: "Are you a 501(c)(3) organization?",
                answer: (
                  <>
                    Yes, we are organized as a charitable organization. See the <a href="https://skroop-public-docs.s3.us-east-1.amazonaws.com/IRS+Determination+Letter" target="_blank" rel="noopener noreferrer" style={{color: 'blue', textDecoration: 'underline'}}>IRS Determination Letter</a>.
                  </>
                ),
              },
              //{
             //   question: "Can I receive a tax deduction for payments I make to Skroop?",
              //  answer: "See Subscriptions below. The Subscription is not tax deductible, but any donations made in excess of that are tax deductible.",
              //}
            ].map((item, index) => (
              <details key={`about-${index}`} className="faq__item">
                <summary>
                  {item.question}
                  <span className="faq__icon" />
                </summary>
                <div className="faq__answer">{item.answer}</div>
              </details>
            ))}
          </div>

          <div className="faq__group">
            <h3 className="faq__group-title">Subscription</h3>
            {[
              {
                question: "Why should I subscribe?",
                answer: "Virtue is hard, but virtue sustains us. We all need a daily reminder of morality.",
              },
              {
                question: "How much does Skroop cost?",
                answer: "Our app is free for all to use. There are two voluntary subscription options available through the iOS mobile app.",
              },
              {
                question: "Is the subscription tax-deductible?",
                answer: "The IRS guidelines generally do not permit tax deductions when you receive something in exchange for payment, but please check with your tax advisor.",
              },
              {
                question: "Can I cancel anytime?",
                answer: "Yes. You can cancel at any time and retain access through the end of your billing period.",
              }
            ].map((item, index) => (
              <details key={`sub-${index}`} className="faq__item">
                <summary>
                  {item.question}
                  <span className="faq__icon" />
                </summary>
                <div className="faq__answer">{item.answer}</div>
              </details>
            ))}
          </div>

        </div>
      </section>

      <Footer />

    {/* Modal goes here, still inside .home-container */}
    {showLoginModal && (
      <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
        <div className="login-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowLoginModal(false)}>×</button>
          <h2>Log in to Skroop</h2>
          {loginError && <div className="login-error" style={{color: 'red', marginBottom: '1rem'}}>{loginError}</div>}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />

            <div className="login-password-container">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                onKeyDown={handleKeyDown}
                className="login-password-input"
              />
              <span
                className="password-toggle-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
            </div>

          <div className="forgot-password">
            <a href="/forgot">Forgot Password?</a>
          </div>
          
            <button type="button" className="login-button" onClick={handleLogin}>
              Log In
            </button>

          <div className="divider">or</div>
          <button className="apple-login" onClick={handleAppleLogin} disabled style={{opacity: 0.6, cursor: 'not-allowed'}}>
            <svg
              className="apple-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="white"
              width="16"
              height="16"
            >
              <path d="M16.7 13.7c-.1-1.7 1.4-2.5 1.5-2.6-0.8-1.2-2.1-1.4-2.5-1.4-1.1-.1-2.1.7-2.6.7s-1.4-.6-2.3-.6c-1.2.1-2.4.7-3 1.7-1.3 2.2-.3 5.4.9 7.2.6.8 1.3 1.7 2.2 1.6.9-.1 1.3-.6 2.4-.6s1.4.6 2.3.6 1.6-.8 2.2-1.6c.7-1 1-2.1 1.1-2.2-.1-.1-2-0.8-2.1-3.2zM14.8 6.1c.5-.6.8-1.4.7-2.2-.7.1-1.6.5-2.1 1.1-.5.6-.8 1.4-.7 2.2.8.1 1.6-.4 2.1-1.1z" />
            </svg>
            Continue with Apple
          </button>
          <p className="signup-link">
            Don’t have an account? <a href="/signup">Sign up</a>
          </p>
        </div>
      </div>
    )}



   </div>

);
}

export default Home;
